import React from 'react';
import { Link } from 'wouter';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '../../lib/utils';
import NavLink, { NAV_ITEMS } from '../NavLink';

export function HeaderBrand({ scrolled, location, isDark }: { scrolled: boolean; location: string; isDark: boolean }) {
  const transparentHome = !scrolled && location === '/';
  const logoSrc = isDark ? '/assets/logo/mef-logo-wt.png' : '/assets/logo/mef-logo.png';

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.35 }}
    >
      <Link
        href="/"
        aria-label="MakersPlace Educational Foundation home"
        className="group flex items-center rounded-xl border border-slate-200/80 bg-white px-2.5 py-1.5 shadow-none transition-all duration-300 hover:-translate-y-0.5 hover:border-slate-300/85 dark:border-slate-700/80 dark:bg-slate-950 dark:hover:border-slate-600/85"
      >
        <img
          src={logoSrc}
          alt="MakersPlace Educational Foundation"
          className={cn(
            'h-12 w-auto object-contain transition-transform duration-300 group-hover:scale-[1.01] sm:h-14 md:h-[4.5rem] lg:h-[4.75rem]',
            transparentHome ? 'brightness-[1.14] contrast-[1.12] saturate-[1.1]' : 'contrast-[1.12] saturate-[1.08]'
          )}
        />
      </Link>
    </motion.div>
  );
}

export function DesktopNav({ isDark, scrolled }: { isDark: boolean; scrolled: boolean }) {
  return (
    <motion.nav
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: 0.05 }}
      className={cn(
        'hidden rounded-2xl px-2.5 py-1.5 md:block',
        isDark
          ? 'bg-slate-950 shadow-none'
          : scrolled
            ? 'bg-white shadow-none'
            : 'bg-white shadow-none'
      )}
    >
      <ul className="flex items-center gap-1">
        {NAV_ITEMS.map((item) => (
          <NavLink
            key={item.href}
            href={item.href}
            label={item.label}
            variant="desktop"
            isDark={isDark}
            scrolled={scrolled}
          />
        ))}
      </ul>
    </motion.nav>
  );
}

export function HeaderActions({ mobileMenuOpen, onToggleMobileMenu }: { mobileMenuOpen: boolean; onToggleMobileMenu: () => void }) {
  return (
    <div className="flex items-center gap-2.5 sm:gap-3">
      <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.1 }}>
        <Link href="/donate" className="hidden md:block">
          <span className="inline-flex h-10 cursor-pointer items-center rounded-full border border-primary/20 bg-primary px-5 text-sm font-semibold text-white shadow-none transition-all duration-300 hover:-translate-y-0.5 hover:brightness-110">
            Donate
          </span>
        </Link>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.12 }}>
        <Link href="/contact" className="hidden md:block">
          <span className="inline-flex h-10 cursor-pointer items-center rounded-full border border-slate-300 bg-white/92 px-5 text-sm font-semibold text-slate-800 transition-all duration-300 hover:-translate-y-0.5 hover:border-primary hover:text-primary dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">
            Get Involved
          </span>
        </Link>
      </motion.div>

      <button
        type="button"
        onClick={onToggleMobileMenu}
        className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-300 bg-white/92 text-slate-700 transition-all duration-300 hover:-translate-y-0.5 hover:border-primary hover:text-primary md:hidden dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
        aria-label="Toggle menu"
        aria-expanded={mobileMenuOpen}
      >
        <svg className="h-6 w-6" stroke="currentColor" fill="none" viewBox="0 0 24 24">
          {mobileMenuOpen ? (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          ) : (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
          )}
        </svg>
      </button>
    </div>
  );
}

export function MobileMenu({ mobileMenuOpen, isDark, mobileMenuRef, onCloseMobileMenu }: { mobileMenuOpen: boolean; isDark: boolean; mobileMenuRef: React.RefObject<HTMLDivElement>; onCloseMobileMenu: () => void }) {
  return (
    <AnimatePresence>
      {mobileMenuOpen && (
        <motion.div
          ref={mobileMenuRef}
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.25 }}
          className={cn('overflow-hidden border-t md:hidden', isDark ? 'border-slate-800 bg-slate-950/98' : 'border-slate-200 bg-white/98')}
          role="dialog"
          aria-modal="true"
          aria-label="Navigation menu"
        >
          <div className="container mx-auto px-4 py-3.5">
            <nav className="flex flex-col space-y-2 rounded-[1.75rem] border border-slate-200/70 bg-slate-50/70 p-3 shadow-[0_14px_36px_rgba(15,23,42,0.06)] dark:border-slate-700 dark:bg-slate-900/50" role="navigation">
              {NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.href}
                  href={item.href}
                  label={item.label}
                  variant="mobile"
                  isDark={isDark}
                  onClick={onCloseMobileMenu}
                />
              ))}
              <Link href="/contact">
                <span
                  className="mt-2 block cursor-pointer rounded-2xl border border-slate-300 bg-white px-4 py-3 text-center font-semibold text-slate-900 transition-all duration-300 hover:border-primary hover:text-primary dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                  onClick={onCloseMobileMenu}
                >
                  Get Involved
                </span>
              </Link>
              <Link href="/donate">
                <span
                  className="block cursor-pointer rounded-2xl border border-primary/20 bg-primary px-4 py-3 text-center font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:brightness-110"
                  onClick={onCloseMobileMenu}
                >
                  Donate
                </span>
              </Link>
            </nav>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
